import React, { useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Link,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import NatureIcon from '@mui/icons-material/Nature';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

export const SUPPORT_EMAIL = 'savaquan.info@gmail.com';
export const PRIVACY_POLICY_URL = '/documents/privacy-policy.html';
/** The section the stores' account-deletion URL points at (/support#delete-account). */
export const DELETE_ACCOUNT_SECTION_ID = 'delete-account';

// The public support page (no sign-in): the App Store's Support URL, and
// where a person finds how to reach SavaQuan and how to delete their account.
const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const { hash } = useLocation();
  const theme = useTheme();

  // The page renders after the browser has looked for the #fragment, so a
  // link straight to a section has to be scrolled to here.
  useEffect(() => {
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    if (id) {
      document.getElementById(id)?.scrollIntoView?.();
    }
  }, [hash]);

  const section = (title: string, body: React.ReactNode, id?: string) => (
    <Card variant="outlined" component="section" id={id} aria-label={title}>
      <CardContent>
        <Typography variant="h6" component="h2" gutterBottom>
          {title}
        </Typography>
        {body}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: theme.palette.grey[50], py: 4 }}>
      <Container maxWidth="md">
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <NatureIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
            <Typography variant="h5" component="span" fontWeight="bold">
              SavaQuan
            </Typography>
          </Box>
          <Button variant="contained" onClick={() => navigate('/')}>
            Home
          </Button>
        </Box>

        <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
          Support
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          SavaQuan is the estimating and job app for tree service companies, on the web, iPhone and Android.
        </Typography>

        <Stack spacing={2}>
          {section('Get help', (
            <>
              <Typography variant="body1" paragraph>
                Email us and we'll get back to you. Tell us which app you use
                (web, iPhone or Android) and the email or phone number you sign in with.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<MailOutlineIcon />}
                href={`mailto:${SUPPORT_EMAIL}?subject=SavaQuan%20support`}
                sx={{ textTransform: 'none' }}
              >
                {SUPPORT_EMAIL}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                SavaQuan LLC, 9169 W State St, Boise, ID 83714
              </Typography>
            </>
          ))}

          {section('Signing in', (
            <Typography variant="body1">
              SavaQuan accounts are created by each company: your company's admin adds you to the team, then you sign
              in with the email or phone number they added. If you can't sign in, or you see that you aren't on a
              team yet, ask your company's admin first.
            </Typography>
          ))}

          {section('Delete your account', (
            <>
              <Typography variant="body1" paragraph>
                In the iPhone or Android app, tap Profile on Home to open Account, then Delete account at the bottom.
                On the web, sign in at savaquan.com, open Settings, then Your account, then Delete account at the
                bottom. You'll confirm it's you and type DELETE.
              </Typography>
              <Typography variant="body1" paragraph>
                Your account closes at once: you're signed out and removed from every team. After 90 days your name,
                email, phone number, address, pay details and sign-in are permanently deleted. Until then we can
                restore your account if you ask. Your companies keep their business records, such as clients,
                estimates, jobs and invoices, which show "Former team member" where they name you.
              </Typography>
              <Typography variant="body1" paragraph>
                Can't use the app or the website? Email{' '}
                <Link href={`mailto:${SUPPORT_EMAIL}?subject=Delete%20my%20SavaQuan%20account`}>{SUPPORT_EMAIL}</Link>
                {' '}from the email address on your account, or tell us the phone number you sign in with, and we'll
                delete it for you.
              </Typography>
              <Typography variant="body1">
                If you're the only owner of a team with other people on it, first make someone else Level 5 (Owner /
                Admin).
              </Typography>
            </>
          ), DELETE_ACCOUNT_SECTION_ID)}

          {section('Privacy', (
            <Typography variant="body1">
              How we collect and use information is described in our{' '}
              <Link href={PRIVACY_POLICY_URL}>Privacy Policy</Link>. See also our{' '}
              <Link component="button" variant="body1" onClick={() => navigate('/terms-of-service')} sx={{ verticalAlign: 'baseline' }}>
                Terms of Service
              </Link>.
            </Typography>
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default SupportPage;
